/**
 * Generates unique instance IDs for folders and events in the sidebar tree.
 * Same folder/event can appear multiple times (via shortcuts), each needs unique ID.
 */

import { EnhancedFolder, EventType } from "../components/types";

export class InstanceIdGenerator {
  // Format: "f:{id}" or "r:{refId}" for references
  static generateFolderInstanceId(
    folder: EnhancedFolder,
    parentPath: string = ""
  ): string {
    const folderSegment = folder.isReference
      ? `r:${folder.referenceId}`
      : `f:${folder.id}`;
    return parentPath ? `${parentPath}/${folderSegment}` : folderSegment;
  }

  // Format: "g:{id}" (gallery) or "r:{refId}" for references
  static generateEventInstanceId(
    event: EventType,
    parentPath: string = ""
  ): string {
    const eventSegment = event.isReference
      ? `r:${event.referenceId}`
      : `g:${event.id}`;
    return parentPath ? `${parentPath}/${eventSegment}` : eventSegment;
  }

  static getFolderPath(
    folder: EnhancedFolder,
    rootFolders: EnhancedFolder[]
  ): string {
    const findPathToFolder = (
      folders: EnhancedFolder[],
      targetFolder: EnhancedFolder,
      currentPath: string[] = []
    ): string[] | null => {
      for (const f of folders) {
        const folderSegment = f.isReference
          ? `r:${f.referenceId}`
          : `f:${f.id}`;
        const newPath = [...currentPath, folderSegment];

        if (
          f === targetFolder ||
          (f.id === targetFolder.id &&
            f.isReference === targetFolder.isReference &&
            f.referenceId === targetFolder.referenceId &&
            f.parentFolderId === targetFolder.parentFolderId)
        ) {
          return newPath;
        }

        if (f.children && f.children.length > 0) {
          const result = findPathToFolder(f.children, targetFolder, newPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findPathToFolder(rootFolders, folder);
    return path ? path.join("/") : this.generateFolderInstanceId(folder);
  }
}
